import { API_BASE as API } from "./apiBase";
import { APP_VERSION } from "../version";

/**
 * Bogue 1202 — Journal navigateur des erreurs de PASSERELLE.
 *
 * Eric, en production : « The origin web server returned an invalid or
 * incomplete response to Cloudflare », immédiatement (2-3 s), en fusionnant
 * des transcriptions ou en corrigeant l'orthographe. Quand l'origine ne
 * répond pas, il n'y a RIEN à journaliser côté serveur, et les logs du
 * conteneur déployé ne sont pas consultables depuis l'interface.
 *
 * Seul le navigateur sait : quelle requête, quel code (520 ≠ 502 ≠ 524), au
 * bout de combien de temps, et avec quel identifiant Cloudflare (`cf-ray`)
 * pour corréler. On envoie donc le constat au backend, en `fetch` (pas en
 * axios : c'est la couche qui vient d'échouer) et sans jamais propager
 * d'erreur — un diagnostic ne doit pas ajouter une panne à la panne.
 */

// 502/503/504 = passerelle ; 520-527 = famille Cloudflare (520 « invalid or
// incomplete response », 524 « timeout »).
const GATEWAY_STATUSES = new Set([502, 503, 504, 520, 521, 522, 523, 524, 525, 526, 527]);

const RATE_LIMIT_MS = 3000;
let _lastSentAt = 0;

export const isGatewayError = (error) => {
  const status = error?.response?.status;
  return typeof status === "number" && GATEWAY_STATUSES.has(status);
};

const extractCloudflareCode = (body) => {
  if (typeof body !== "string") return "";
  const m = body.match(/Error\s*(\d{3})/i) || body.match(/"error_code"\s*:\s*"?(\d{3})/i);
  return m ? m[1] : "";
};

export const buildGatewayReport = (error) => {
  const cfg = error?.config || {};
  const res = error?.response || {};
  const body = typeof res.data === "string" ? res.data : "";
  const startedAt = cfg._startedAt || 0;
  return {
    url: String(cfg.url || "").slice(0, 400),
    method: String(cfg.method || "").slice(0, 10),
    status: res.status || 0,
    duration_ms: startedAt ? Math.round(Date.now() - startedAt) : 0,
    cf_ray: String(res.headers?.["cf-ray"] || "").slice(0, 80),
    cf_error_code: extractCloudflareCode(body),
    body_snippet: body.replace(/\s+/g, " ").slice(0, 400),
    page_url: String(window.location?.href || "").slice(0, 300),
    bundle_version: String(APP_VERSION || "").slice(0, 120),
    user_agent: String(navigator?.userAgent || "").slice(0, 300),
  };
};

export const reportGatewayError = (error, { fetchFn = fetch, now = () => Date.now() } = {}) => {
  try {
    if (!isGatewayError(error)) return false;
    // Anti-flood : une page qui relance 12 requêtes ne doit pas générer 12
    // rapports identiques.
    if (now() - _lastSentAt < RATE_LIMIT_MS) return false;
    _lastSentAt = now();
    fetchFn(`${API}/api/sentinel/gateway-error`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(buildGatewayReport(error)),
      keepalive: true,
    }).catch(() => {});
    return true;
  } catch {
    return false;
  }
};

export default reportGatewayError;
